import React from 'react';
import cx from 'classnames';
import Link from 'next/link';

import styles from '../StaticContents.module.scss';

const CodeOfConduct: React.FC = () => {
	return (
		<div className={cx(styles.staticContents, styles.container)}>
			<h1>Dealer Code of Conduct</h1>
			<p>
				<strong>This Code was last updated on 8th October 2024</strong>
			</p>
			<p>
				At Motorway, we’re committed to fostering a trustworthy, safe, reliable and positive experience for everyone
				using our platform. We can’t do this without the help of our Dealer partners. The high standards of service
				offered to Sellers on Motorway extends to the last mile of the transaction – post-sale communication, document
				collection, Vehicle collection, and the payment process. With this in mind, we created this Dealer Code of
				Conduct (the <strong>“Code”</strong>) as a set of guidelines to help Dealers know what we expect of them.{' '}
			</p>
			<p>
				When you sign up to use the Motorway Platform, in addition to our Pro Terms (defined below), you agree to comply
				with the Code. It is mandatory for all Dealers and it must be adhered to. Any breach of the Code may result in
				suspension (temporarily disabling your ability to bid on Vehicles) and/or termination (permanent removal from
				the Motorway Platform).{' '}
			</p>
			<p>
				Any capitalised terms not defined within the Code shall have the meanings given to them in the Motorway pro
				terms and conditions, available at <Link href="/terms">https://pro.motorway.co.uk/terms</Link> (the{' '}
				<strong>“Pro Terms”</strong>).{' '}
			</p>
			<p>
				References to “you”, “your” or “yourself” are references to you as Dealer, your employees and anyone that you
				engage to act on your behalf (including for example, collection drivers) and references to “us”, “we”, “our” or
				“Motorway” shall refer to Motorway Online Ltd.{' '}
			</p>
			<h3>Unprofessional and unacceptable practices</h3>
			<p>
				You must act in a professional, honest and fair manner, and comply with the Pro Terms and any guidelines or
				policies at all times. We will not tolerate unprofessional and unacceptable behaviour, or any fraudulent or
				criminal conduct of any kind.
			</p>
			<p>Examples of unprofessional and unacceptable behaviour include but are not limited to:</p>
			<ul data-indent>
				<li>
					Attempting to use the Motorway Platform as an individual Seller (i.e., without using Motorway Trade) to sell
					Vehicles, which is a material breach of the Pro Terms and may also amount to fraudulent misrepresentation
				</li>
				<li>
					Failing to contact a Seller (or us where Motorway Move is to be used) to arrange for collection of the Vehicle
					at all or in a timely manner{' '}
				</li>
				<li>
					Changing a Seller’s collection date with little notice or arriving to collect a Vehicle at a time or date that
					has not been agreed with the Seller in advance, including collections at unsociable hours.
				</li>
				<li>
					Cancelling Vehicle purchases in breach of the Cancellation and Price Adjustments Policy and Pro Terms. We may
					investigate cancellations, including high cancellation rates and instances where you cancel purchases of
					Vehicles without having completed a Dealer Cancellation and Price Adjustment Form for genuine reasons
				</li>
				<li>
					Cancelling, or encouraging the Seller to cancel, a Vehicle purchase/sale (as applicable) and then proceeding
					to purchase that Vehicle directly from the Seller{' '}
				</li>
				<li>Intimidation or aggressive behaviour towards a Seller or members of the public on collection</li>
				<li>
					Haggling or persistent attempts to unfairly, or excessively, reduce the Vehicle offer on collection without
					adequate justification and without using the Dealer Cancellation and Price Adjustment Form
				</li>
				<li>Failing to make payment of the Purchase Price to the Seller during the collection of the Vehicle</li>
				<li>
					Failing to discharge any Outstanding Finance on a Vehicle in full and to obtain proof of such payment from the
					applicable finance company,
				</li>
				<li>Purchasing Vehicles on behalf of third parties</li>
				<li>
					Impersonation of any party other than your dealership, for example to falsely cancel sales, which may amount
					to fraudulent misrepresentation
				</li>
			</ul>
			<h3>Respectful behaviour and communication</h3>
			<p>
				At Motorway, we’re proud of our Dealer partners and the positive selling experience that we together achieve for
				Sellers.
			</p>
			<p>
				You agree to conduct yourself at all times in your relations with members of the public, us, our employees, or
				anyone acting on our behalf, Sellers, other Dealers and any persons involved with our Transport Services with
				respect and to act with honesty and integrity.{' '}
			</p>
			<p>
				We will not tolerate intimidating, offensive, aggressive or abusive behaviour. You must not engage in any form
				of harassment, whether it be physical, sexual, psychological or verbal. This applies to all of your activities,
				including but not limited to:
			</p>
			<ul data-indent>
				<li>
					Communication between you and Motorway (or anyone that acts on Motorway’s behalf), you and other Dealers, and
					you and Sellers or members of the public{' '}
				</li>
				<li>Information provided about your dealership online, including on social media</li>
				<li>
					Your professional or personal conduct outside of being a Dealer using the Motorway Platform including, for
					example, on social media accounts
				</li>
			</ul>
			<p>
				Whilst we do not condone disrespectful or offensive behaviour from Sellers towards you, we ask that you always
				conduct yourself in resolving any conflict in a professional manner, for example, by proactively de-escalating
				conflicts or, where necessary, removing yourselves from the premises to prevent any further escalation.{' '}
			</p>
			<p>
				We will investigate all reports of misconduct. If we need more information about a misconduct complaint, our
				team will contact you. We may also require further evidence or information to settle the complaint, which we
				will request from you.
			</p>
			<h3>Consequences for failure to comply with the Code</h3>
			<p>
				Whilst we will aim to resolve issues with you on an informal basis where possible, we will not tolerate
				unprofessional conduct and failure to comply with the Code may result in the following, subject to our sole
				discretion:{' '}
			</p>
			<ul data-indent>
				<li>
					Suspension - we may temporarily restrict your access to the Motorway Platform, disabling your ability to place
					a Bid on Vehicles and cancelling any live Bids on Vehicles, where you have committed a less serious breach of
					the Code or whilst we investigate any potential breach of the Code. Where you have been instructed to do so,
					we would expect any live Vehicle sales to be completed by you whilst you are suspended
				</li>
				<li>
					Termination - we may also decide to end our business relationship, permanently banning you from the Motorway
					Platform, where we have reason to believe that you have committed a serious breach, or repeated breaches, of
					the Code
				</li>
			</ul>
			<p>
				Additionally, should we have concerns about your conduct such that you have failed to comply with the Code, we
				may notify you that you are only entitled to continue to use the Motorway Platform on the condition that you
				comply with any Minimum Usage Requirements notified to you.
			</p>
			<h3>Your feedback</h3>
			<p>
				To keep the Code relevant, we welcome any feedback that you have. If you have any concerns or questions, please
				reach out to Dealer Support or your Account Manager.
			</p>
			<p>
				If you’re aware of certain behaviour taking place that doesn’t align with the Code, please email{' '}
				<Link href="mailto:conduct@motorway.co.uk">conduct@motorway.co.uk</Link>. We expect any allegations of potential
				violations of the Code to be made in good faith and any information shared with us will be treated with the
				strictest confidence.{' '}
			</p>
		</div>
	);
};

export default CodeOfConduct;
